/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/IntelligenceLane.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-INTELLIGENCE-001
 * PURPOSE: Show the top-2 ranked structured signals plus the "+N" manager door.
 * ROLE IN ARCHITECTURE: Bounded lane leaf of the expanded triage context.
 * MAIN EXPORTS: IntelligenceLane.
 * NON-RESPONSIBILITIES: No ranking rules (laneModel owns them), no overlay.
 * NOTES FOR FUTURE AI: Copy acks + clipboard-denied fallbacks live INSIDE
 *   VerificationCodeChip / InvoiceNumberChip (1.5s Copied contract) — reuse,
 *   never re-implement. Everything beyond the top-2 goes through "+N".
 *   Invoice chip opens structured bill via EmailStatementViewer when the
 *   tenant host wired it (change #432) — do not import @quotech/bills here.
 */
import type { EmailActionSignal } from '../../action-signals'
import {
  InvoiceNumberChip,
  VerificationCodeChip,
} from '../../action-signals'
import { EmailTagChips } from '../../components/EmailTagChips'
import {
  isStructuredDocCategory,
  useEmailStatementViewer,
} from '../../components/StatementViewer'
import { SenderSignalBadges } from '../../sender-identity'
import type { Email } from '../../hooks/useEmails'
import { cn } from '../../../_internal/lib/utils'
import { LANE_CONTROL_CLASS } from './laneControl'
import { selectIntelligenceSignals } from './laneModel'
import { LaneShell, type LaneShellProps } from './LaneShell'

export interface IntelligenceLaneProps
  extends Pick<LaneShellProps, 'surfaceProps' | 'onOpenEllipsis' | 'expand'> {
  email: Email
  verificationCode: EmailActionSignal | null
  invoiceNumber: EmailActionSignal | null
  onDismissVerification: () => void
  onOpenSignalsManager: () => void
}

/**
 * Renders the Intelligence lane.
 *
 * Inputs: email, pre-resolved verification/invoice signals (already
 * dismissal-filtered by the host), manager-open callback.
 * Output: at most two ranked signal groups inline + "+N" overflow button.
 * Failure mode: zero signals renders the "Add tag" affordance so tagging
 * depth (operator 2026-07-15) keeps its always-visible entry point.
 */
export function IntelligenceLane({
  email,
  surfaceProps,
  onOpenEllipsis,
  verificationCode,
  invoiceNumber,
  onDismissVerification,
  onOpenSignalsManager,
  expand,
}: IntelligenceLaneProps) {
  const { openStructuredStatement } = useEmailStatementViewer()
  const openBillStructure =
    openStructuredStatement && isStructuredDocCategory(email.category)
      ? () => openStructuredStatement(String(email.id))
      : undefined

  const selection = selectIntelligenceSignals(email, {
    hasVerification: verificationCode != null,
    hasInvoice: invoiceNumber != null,
  })

  const renderGroup = (kind: string) => {
    switch (kind) {
      case 'verification':
        return verificationCode ? (
          <span key={kind} data-reader-chip="verification" className="inline-flex min-w-0">
            <VerificationCodeChip
              emailId={email.id}
              signal={verificationCode}
              onRejected={onDismissVerification}
            />
          </span>
        ) : null
      case 'invoice':
        return invoiceNumber ? (
          <span key={kind} data-reader-chip="invoice" className="inline-flex min-w-0">
            <InvoiceNumberChip
              signal={invoiceNumber}
              onOpenStructure={openBillStructure}
            />
          </span>
        ) : null
      case 'provenance':
        return (
          <span key={kind} data-reader-chip="provenance" className="inline-flex min-w-0 gap-1">
            <SenderSignalBadges email={email} />
          </span>
        )
      case 'tags':
        // flex-1 (not inline-flex/shrink-to-fit) so a dense tag set is forced
        // to exactly the lane's remaining width BEFORE flex-wrap decides
        // where to break — shrink-to-fit's content-then-shrink math left a
        // ~3px rounding overrun at 660px with 20 tags (WORK ITEM 0).
        return (
          <span key={kind} data-reader-chip="tag" className="flex min-w-0 flex-1">
            <EmailTagChips emailId={email.id} tags={email.tags} size="xs" />
          </span>
        )
      case 'ai_summary':
        return (
          <button
            key={kind}
            type="button"
            data-testid="ai-summary-signal"
            title={email.ai_summary || undefined}
            onClick={onOpenSignalsManager}
            className={cn(
              LANE_CONTROL_CLASS,
              'max-w-[180px] bg-muted text-muted-foreground hover:bg-accent',
            )}
          >
            {/* truncate has to live on the child: the control is inline-flex,
                so clipping the container itself would do nothing. */}
            <span className="min-w-0 truncate">AI: {email.ai_summary}</span>
          </button>
        )
      default:
        return null
    }
  }

  return (
    <LaneShell
      lane="intelligence"
      surfaceProps={surfaceProps}
      onOpenEllipsis={onOpenEllipsis}
      expand={expand}
      trailing={
        selection.hiddenCount > 0 ? (
          <button
            type="button"
            data-testid="intelligence-overflow"
            aria-label={`Show ${selection.hiddenCount} more signals`}
            onClick={onOpenSignalsManager}
            className={cn(
              LANE_CONTROL_CLASS,
              'bg-muted font-medium text-muted-foreground hover:bg-accent',
            )}
          >
            +{selection.hiddenCount}
          </button>
        ) : selection.totalCount === 0 ? (
          <button
            type="button"
            data-testid="intelligence-add-tag"
            onClick={onOpenSignalsManager}
            className={cn(
              LANE_CONTROL_CLASS,
              'border border-dashed border-border text-muted-foreground hover:bg-accent',
            )}
          >
            Add tag
          </button>
        ) : null
      }
    >
      {selection.visible.map((group) => renderGroup(group.kind))}
    </LaneShell>
  )
}
